import { useTranslation } from "react-i18next";

import { ActivityRow, useActivityFeed } from "@/entities/activity";
import { useReadAll } from "@/features/read-all";
import { staggerIndex } from "@/shared/lib";
import { Button, useToast } from "@/shared/ui";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";
import { TopBar } from "@/widgets/top-bar";

/**
 * Уведомления. Figma: `Activity · Notifications` (25:163).
 *
 * Отдельной ручки уведомлений в API нет — есть та же лента с флагом
 * `is_read`. Поэтому экран показывает непрочитанные события из ленты `all` и
 * даёт единственное действие, которое контракт предусматривает, —
 * `POST /activity/read-all`. Фильтровать по `is_read` на сервере нельзя, так
 * что отбор идёт по уже загруженным страницам.
 */
export function NotificationsPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const { data, isPending, isError, refetch } = useActivityFeed("all");
  const readAll = useReadAll();

  if (isPending) return <ScreenSkeleton blocks={[92, 92, 92]} />;

  if (isError) {
    return (
      <ScreenState
        tone="loss"
        icon="warningFill"
        title={t("states.error.title")}
        body={t("states.error.body")}
        action={{ label: t("states.error.action"), onClick: () => void refetch() }}
      />
    );
  }

  const unread = data.pages.flatMap((page) => page.items).filter((item) => !item.is_read);

  return (
    <div className="flex flex-col gap-3 px-5 py-3">
      <TopBar title={t("activity.notifications")} subtitle={t("activity.notificationsSubtitle")} />

      {unread.length === 0 ? (
        <ScreenState
          tone="profit"
          icon="checkFat"
          title={t("activity.allRead.title")}
          body={t("activity.allRead.body")}
        />
      ) : (
        <>
          <Button
            size="cta"
            variant="secondary"
            block
            loading={readAll.isPending}
            onClick={() =>
              readAll.mutate(undefined, {
                onSuccess: (result) => {
                  toast.show({
                    tone: "profit",
                    title: t("activity.markedRead", { count: result.marked }),
                  });
                },
                onError: () => {
                  toast.show({ tone: "loss", title: t("states.error.title") });
                },
              })
            }
          >
            {t("activity.readAll")}
          </Button>

          <ul className="flex flex-col gap-3">
            {unread.map((item, index) => (
              <li key={item.id} className="stagger" style={staggerIndex(index)}>
                <ActivityRow item={item} />
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
